<template>
  <Teleport to="body">
    <Transition name="fade">
      <div
        v-show="isVisible"
        class="call_container rounded-lg text_color font-medium w-80"
        :style="modalStyle"
        @mousedown="startDrag"
        @touchstart="startDrag"
      >
        <slot></slot>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: true,
  },
});

const initialPosition = {
  right: 10,
  bottom: 10,
};

const position = ref({ ...initialPosition });
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });

const modalStyle = computed(() => ({
  right: `${position.value.right}px`,
  bottom: `${position.value.bottom}px`,
  transition: isDragging.value ? "none" : "all 0.3s ease",
}));

const startDrag = (event) => {
  try {
    if (
      event.target.closest(".end_call") ||
      event.target.closest(".receive_call") ||
      event.target.closest(".call_action_container") ||
      event.target.closest(".handle_call")
    ) {
      return;
    }

    isDragging.value = true;
    const clientX = event.clientX || event.touches[0].clientX;
    const clientY = event.clientY || event.touches[0].clientY;

    const containerElement = document.querySelector(".call_container");
    if (!containerElement) return;

    const rect = containerElement.getBoundingClientRect();

    dragStart.value = {
      x: clientX,
      y: clientY,
      initialRight: position.value.right,
      initialBottom: position.value.bottom,
    };

    document.addEventListener("mousemove", drag);
    document.addEventListener("mouseup", stopDrag);
    document.addEventListener("touchmove", drag);
    document.addEventListener("touchend", stopDrag);
  } catch (error) {
    console.error("An error occurred while starting drag: ", error);
  }
};

const drag = (event) => {
  try {
    if (isDragging.value) {
      const clientX = event.clientX || event.touches[0].clientX;
      const clientY = event.clientY || event.touches[0].clientY;

      const deltaX = dragStart.value.x - clientX;
      const deltaY = dragStart.value.y - clientY;

      const containerElement = document.querySelector(".call_container");
      if (!containerElement) return;

      const rect = containerElement.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;

      const newRight = Math.max(
        10,
        Math.min(window.innerWidth - 10, dragStart.value.initialRight + deltaX),
      );

      const newBottom = Math.max(
        10,
        Math.min(
          window.innerHeight - 10,
          dragStart.value.initialBottom + deltaY,
        ),
      );

      const effectiveRight = Math.min(
        newRight,
        window.innerWidth - containerWidth - 10,
      );
      const effectiveBottom = Math.min(
        newBottom,
        window.innerHeight - containerHeight - 10,
      );

      position.value = {
        right: Math.max(10, effectiveRight),
        bottom: Math.max(10, effectiveBottom),
      };
    }
  } catch (error) {
    console.error("An error occurred while handling drag: ", error);
  }
};

const stopDrag = () => {
  try {
    isDragging.value = false;
    document.removeEventListener("mousemove", drag);
    document.removeEventListener("mouseup", stopDrag);
    document.removeEventListener("touchmove", drag);
    document.removeEventListener("touchend", stopDrag);
  } catch (error) {
    console.error("An error occurred while stopping drag: ", error);
  }
};

onUnmounted(() => {
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", drag);
  document.removeEventListener("touchend", stopDrag);
});
</script>

<style scoped>
.call_container {
  background-color: var(--draggable-modal-bg);
  box-shadow: 0 2px 8px var(--black-opacity-15);
  position: fixed;
  user-select: none;
  cursor: move;
  z-index: 1000;
  height: fit-content;
  will-change: height, right, bottom;
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.3s ease,
    height 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
